import { useState } from 'react';
import {
  useAdminWorkspaces,
  useAdminPlans,
  useAdminWorkspace,
  useUpdateWorkspaceSubscription,
} from '../lib/queries.js';

export function WorkspacesPage() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const { data, isLoading } = useAdminWorkspaces(search);

  // Modal State
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const { data: details, isLoading: isLoadingDetails } = useAdminWorkspace(selectedWorkspaceId);
  const { data: plansData } = useAdminPlans();
  const updateSubscription = useUpdateWorkspaceSubscription();

  // Form State
  const [selectedPlan, setSelectedPlan] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isLifetime, setIsLifetime] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Open manage modal
  const handleManage = (wsId: string) => {
    setSelectedWorkspaceId(wsId);
    setSuccessMsg('');
    setErrorMsg('');
    // Initialize form states once details are loaded
  };

  // Close modal
  const handleClose = () => {
    setSelectedWorkspaceId(null);
    setSelectedPlan('');
    setExpiresAt('');
    setIsLifetime(false);
    setSuccessMsg('');
    setErrorMsg('');
  };

  // Effect-like behavior on details loaded
  if (details && selectedPlan === '' && !successMsg && !errorMsg) {
    if (details.subscription) {
      setSelectedPlan(details.subscription.planSlug || 'free');
      if (details.subscription.currentPeriodEnd) {
        // Format to YYYY-MM-DD
        const d = new Date(details.subscription.currentPeriodEnd);
        const formatted = d.toISOString().split('T')[0] || '';
        setExpiresAt(formatted);
        setIsLifetime(false);
      } else {
        setExpiresAt('');
        setIsLifetime(true);
      }
    } else {
      setSelectedPlan('free');
      setExpiresAt('');
      setIsLifetime(true);
    }
  }

  const handleSaveSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWorkspaceId) return;

    setSuccessMsg('');
    setErrorMsg('');

    try {
      await updateSubscription.mutateAsync({
        workspaceId: selectedWorkspaceId,
        planSlug: selectedPlan,
        expiresAt: isLifetime ? null : expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      setSuccessMsg('Assinatura salva com sucesso!');
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao salvar assinatura.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Workspaces</h1>
          <p className="text-xs text-zinc-400 mt-1">Gerencie os espaços de trabalho do sistema e atribua planos de uso.</p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(q);
        }}
        className="flex gap-2 bg-zinc-950/20 p-1 rounded-lg border border-zinc-800/40"
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome ou slug do workspace..."
          className="flex-1 rounded-md bg-zinc-950 border border-zinc-850 px-3.5 py-2 text-xs text-zinc-200 focus:outline-none focus:border-cyan-500/50 transition-all font-semibold"
        />
        <button
          type="submit"
          className="bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold px-4 py-2 rounded-md transition-all shadow-md cursor-pointer"
        >
          Buscar
        </button>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-800/60 bg-zinc-950/20 backdrop-blur-md overflow-hidden shadow-xl">
          <table className="w-full text-xs text-left">
            <thead className="bg-zinc-950/50 text-[10px] uppercase font-bold text-zinc-400 tracking-wider border-b border-zinc-800/50">
              <tr>
                <th className="py-3 px-4">Nome / ID</th>
                <th className="py-3 px-4">Slug</th>
                <th className="py-3 px-4">Proprietário (Owner)</th>
                <th className="py-3 px-4">Assinatura</th>
                <th className="py-3 px-4">Criado Em</th>
                <th className="py-3 px-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40">
              {data?.workspaces.map((w) => (
                <tr key={w.id} className="hover:bg-zinc-900/20 transition-all">
                  <td className="py-3.5 px-4">
                    <span className="font-bold text-zinc-200 block">{w.name}</span>
                    <span className="text-[10px] text-zinc-500 font-mono block mt-0.5">{w.id}</span>
                  </td>
                  <td className="py-3.5 px-4 font-mono text-cyan-400/90 font-semibold">{w.slug}</td>
                  <td className="py-3.5 px-4 text-zinc-300 font-medium">{w.ownerEmail ?? '—'}</td>
                  <td className="py-3.5 px-4">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        w.subStatus === 'active' || w.subStatus === 'trialing'
                          ? 'bg-cyan-950/50 text-cyan-400 border border-cyan-900/50'
                          : 'bg-zinc-900/60 text-zinc-400 border border-zinc-800/60'
                      }`}
                    >
                      {w.subStatus ? w.subStatus.toUpperCase() : 'FREE / NENHUMA'}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-zinc-450">{new Date(w.createdAt).toLocaleDateString()}</td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      onClick={() => handleManage(w.id)}
                      className="bg-zinc-900 hover:bg-zinc-850 text-cyan-400 hover:text-cyan-300 border border-zinc-800 hover:border-cyan-950 text-[11px] font-bold px-3 py-1 rounded transition-all cursor-pointer shadow-sm"
                    >
                      Gerenciar
                    </button>
                  </td>
                </tr>
              ))}
              {data?.workspaces.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-zinc-500 font-semibold">
                    Nenhum workspace encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Subscription/Details Management Modal */}
      {selectedWorkspaceId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            
            {/* Header */}
            <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-950/80">
              <div>
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">Gerenciar Workspace</h2>
                <p className="text-[10px] text-zinc-400 mt-0.5">Defina limites, altere planos e gerencie a assinatura.</p>
              </div>
              <button
                onClick={handleClose}
                className="text-zinc-400 hover:text-zinc-200 text-xs font-semibold bg-zinc-900 hover:bg-zinc-850 px-2.5 py-1 rounded transition-all cursor-pointer"
              >
                Fechar
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto space-y-5 flex-1">
              {isLoadingDetails ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
                </div>
              ) : details ? (
                <div className="space-y-6">
                  
                  {/* Info Card */}
                  <div className="bg-zinc-900/30 border border-zinc-850 p-3.5 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase block font-bold">Nome</span>
                        <span className="text-zinc-200 font-semibold">{details.workspace.name}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase block font-bold">Slug</span>
                        <span className="text-cyan-400 font-mono font-semibold">{details.workspace.slug}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-zinc-850/60">
                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase block font-bold">Owner</span>
                        <span className="text-zinc-200 font-semibold">{details.workspace.ownerEmail ?? '—'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase block font-bold">Criado em</span>
                        <span className="text-zinc-300">{new Date(details.workspace.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Plan editor card */}
                  <form onSubmit={handleSaveSubscription} className="space-y-4">
                    <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-lg space-y-4">
                      <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider pb-1.5 border-b border-zinc-850/60">
                        Alterar Plano / Assinatura
                      </h3>

                      <div>
                        <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-1">Selecione o Plano</label>
                        <select
                          value={selectedPlan}
                          onChange={(e) => setSelectedPlan(e.target.value)}
                          className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-cyan-500/50 font-semibold"
                        >
                          {plansData?.plans.map((p) => (
                            <option key={p.id} value={p.slug}>
                              {p.name} (R$ {(p.priceBrl / 100).toFixed(2)})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] uppercase text-zinc-500 font-bold block">Expiração</label>
                          <label className="flex items-center gap-1 text-[11px] text-zinc-450 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={isLifetime}
                              onChange={(e) => setIsLifetime(e.target.checked)}
                              className="w-3.5 h-3.5 rounded border-zinc-850 bg-zinc-950 text-cyan-500 focus:ring-0"
                            />
                            Vitalício / Sem expiração
                          </label>
                        </div>

                        {!isLifetime && (
                          <input
                            type="date"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            required
                            className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-cyan-500/50 font-semibold"
                          />
                        )}
                      </div>
                    </div>

                    {successMsg && (
                      <div className="bg-emerald-950/20 border border-emerald-900/50 p-2.5 rounded text-xs text-emerald-400 font-bold text-center">
                        {successMsg}
                      </div>
                    )}

                    {errorMsg && (
                      <div className="bg-red-950/20 border border-red-900/50 p-2.5 rounded text-xs text-red-400 font-bold text-center">
                        {errorMsg}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={updateSubscription.isPending}
                      className="w-full bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold py-2 rounded-md transition-all shadow-md flex items-center justify-center gap-1 disabled:opacity-50 cursor-pointer"
                    >
                      {updateSubscription.isPending ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-black"></div>
                          Salvando...
                        </>
                      ) : (
                        'Salvar Assinatura'
                      )}
                    </button>
                  </form>

                </div>
              ) : (
                <div className="text-center py-6 text-zinc-500">Erro ao carregar detalhes.</div>
              )}
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
}

